import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import {
  Box,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";

interface ImagePreviewProps {
  src?: string | null;
  alt: string;
  placeholderTitle?: string;
  placeholderDescription?: string;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

const mergeSx = (base: SxProps<Theme>, extra?: SxProps<Theme>) => {
  if (!extra) return base;
  return Array.isArray(extra) ? [base, ...extra] : [base, extra];
};

export const ImagePreview = ({
  src,
  alt,
  placeholderTitle = "No image selected",
  placeholderDescription = "Upload an image or paste a URL to preview it here.",
  sx,
  onClick,
}: ImagePreviewProps) => {
  const hasImage = Boolean(src && src.trim().length > 0);

  if (hasImage) {
    return (
      <Box
        component="img"
        src={src ?? undefined}
        alt={alt}
        onClick={onClick}
        sx={mergeSx(
          {
            width: "100%",
            maxHeight: 280,
            objectFit: "contain",
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            cursor: onClick ? "pointer" : "default",
          },
          sx
        )}
      />
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={mergeSx(
        {
          width: "100%",
          minHeight: 160,
          px: 2,
          py: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          borderRadius: 2,
          border: (theme) => `1px dashed ${theme.palette.divider}`,
          color: (theme) => theme.palette.text.secondary,
          backgroundColor: (theme) => theme.palette.action.hover,
          cursor: onClick ? "pointer" : "default",
        },
        sx
      )}
    >
      <Stack spacing={0.5} alignItems="center">
        <BrokenImageIcon color="disabled" fontSize="large" />
        <Typography variant="subtitle2">{placeholderTitle}</Typography>
        <Typography variant="body2" color="text.secondary">
          {placeholderDescription}
        </Typography>
      </Stack>
    </Box>
  );
};
